/**
 * URL builder utility for constructing API endpoints
 * Provides type-safe URL construction with parameter substitution
 */

import { API_CONFIG } from '@/config/api'

export class ApiUrlBuilder {
  private baseUrl: string

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG.BASE_URL
  }

  /**
   * Build a complete URL from an endpoint
   * @param endpoint - The API endpoint (e.g., '/patients/')
   * @param params - Parameters to substitute in the URL
   * @returns Complete URL
   */
  build(endpoint: string, params?: Record<string, string | number>): string {
    const base = this.baseUrl.replace(/\/+$/, '')
    const path = endpoint.replace(/^\/+/, '')
    let url = path ? `${base}/${path}` : base
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, String(value))
      })
    }
    
    return url
  }

  /**
   * Build URL with query parameters
   * @param endpoint - The API endpoint
   * @param queryParams - Query parameters to append
   * @returns URL with query string
   */
  buildWithQuery(
    endpoint: string, 
    queryParams: Record<string, string | number | boolean | undefined>
  ): string {
    const url = this.build(endpoint)
    const searchParams = new URLSearchParams()
    
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value))
      }
    })
    
    const queryString = searchParams.toString()
    return queryString ? `${url}?${queryString}` : url
  }

  /**
   * Build URL for paginated endpoints
   * @param endpoint - The API endpoint
   * @param page - Page number
   * @param pageSize - Items per page
   * @param additionalParams - Additional query parameters
   * @returns URL with pagination parameters
   */
  buildPaginated(
    endpoint: string,
    page: number = 1,
    pageSize: number = 20,
    additionalParams?: Record<string, string | number | boolean>
  ): string {
    return this.buildWithQuery(endpoint, {
      page,
      page_size: pageSize,
      ...additionalParams,
    })
  }

  /**
   * Build URL for search endpoints
   * @param endpoint - The API endpoint
   * @param searchTerm - Search term
   * @param ordering - Ordering field
   * @param additionalParams - Additional query parameters
   * @returns URL with search parameters
   */
  buildSearch(
    endpoint: string,
    searchTerm?: string,
    ordering?: string,
    additionalParams?: Record<string, string | number | boolean>
  ): string {
    return this.buildWithQuery(endpoint, {
      ...(searchTerm && { search: searchTerm }),
      ...(ordering && { ordering }),
      ...additionalParams,
    })
  }
}

// Default instance
export const apiUrlBuilder = new ApiUrlBuilder()

// Factory function for creating builders with different base URLs
export const createApiUrlBuilder = (baseUrl: string): ApiUrlBuilder => {
  return new ApiUrlBuilder(baseUrl)
}

// Helper functions for common URL patterns
export const buildPatientUrl = (id: string): string => {
  return apiUrlBuilder.build(API_CONFIG.ENDPOINTS.PATIENTS.DETAIL(id))
}

export const buildAssessmentUrl = (id: string): string => {
  return apiUrlBuilder.build(API_CONFIG.ENDPOINTS.ASSESSMENTS.DETAIL(id))
}

export const buildPatientAssessmentsUrl = (patientId: string): string => {
  return apiUrlBuilder.build(API_CONFIG.ENDPOINTS.PATIENTS.ASSESSMENTS(patientId))
}

export const buildAssessmentReportUrl = (id: string, reportType: string): string => {
  return apiUrlBuilder.build(API_CONFIG.ENDPOINTS.ASSESSMENTS.REPORT(id, reportType))
}

export const buildPatientAssessmentUrl = (id: string, token: string): string => {
  return apiUrlBuilder.build(API_CONFIG.ENDPOINTS.PATIENT.ASSESSMENT(id, token))
}






